(function(app) {
    'use strict';

    /**
     * Centralized data manager to ensure consistency between price calculation and cart data
     */
    app.DataManager = {
        /**
         * Calculate elements based on text for text builders
         * @param {string} text The text content
         * @param {string} builderType The builder type
         * @returns {number} The calculated number of elements
         */
        calculateTextElements(text, builderType) {
            // Only apply character-based element calculation for text builders
            if (!['text', 'image-text', 'flag'].includes(builderType)) {
                return 1; // Default element count for non-text builders
            }

            if (!text || text.trim() === '') {
                return 1; // Minimum of 1 element even with no text
            }

            // Count characters (excluding spaces for more accurate pricing)
            const characterCount = text.trim().length;


            // Calculate elements: each character = 1.5 elements
            let calculatedElements = characterCount * 1.5;
            
            
            //Tilføj et ekstra element hvis der er tale om en flag builder
            if(builderType == "flag" && calculatedElements){
                calculatedElements++;
            }

            // Always round up and ensure minimum of 1 element
            return Math.max(1, Math.ceil(calculatedElements));
        },

        /**
         * Get unified product data for both price calculation and cart submission
         * @returns {object} The unified product data
         */
        getUnifiedData() {
            const data = {};
            
            // Get data from all components EXCEPT DataManager itself
            const allComponents = app.Store.getState('builderAvailableComponents') || [];

            allComponents.forEach(componentName => {
                // Skip DataManager to avoid circular reference
                if (componentName === 'DataManager') return;
                
                const component = app[componentName];
                if (component && typeof component.getCartData === 'function') {
                    const componentData = component.getCartData();
                    Object.assign(data, componentData);
                }
            });

            console.log('getUnifiedData:');
            console.log(data);
            
            // Ensure consistent field names
            return this.normalizeData(data);
        },
        
        /**
         * Normalize data to ensure consistent field names
         * @param {object} data Raw data from components
         * @returns {object} Normalized data
         */
        normalizeData(data) {
            const builderType = app.Store.getState('builderType');
            const isTextBuilder = ['text', 'image-text', 'flag', 'sticker-2v2-layout', 'sticker-2v3-layout'].includes(builderType);
            
            const normalized = {
                // Core fields
                quantity: data.quantity || 1,
                productId: app.Store.getState('productId'),
            };

            // Preserve generated preview payloads used by cart/checkout thumbnails.
            if (data.customProductImage) normalized.customProductImage = data.customProductImage;
            if (data.previewImage) normalized.previewImage = data.previewImage;


            // Calculate elements based on text for text builders
            if (isTextBuilder && data.text) {
                const calculatedElements = this.calculateTextElements(data.text, builderType);
                normalized.stickerElements = calculatedElements;
                
                // Add debug info for development
                if (window.console && console.log) {
                    console.log(`Text Elements Calculation:`, {
                        text: data.text,
                        characterCount: data.text.replace(/\s/g, '').length,
                        calculatedElements: calculatedElements,
                        builderType: builderType
                    });
                }
            } else {
                // For non-text builders, use the original stickerElements
                normalized.stickerElements = app.Store.getState('stickerElements') || 1;
            }
            
            // Only include text fields for text-based builders
            if (isTextBuilder) {
                if (data.text) normalized.text = data.text;
                if (data.textWidth) normalized.textWidth = data.textWidth;
                if (data.textHeight) normalized.textHeight = data.textHeight;
                if (data.textBaseHeight) normalized.textBaseHeight = data.textBaseHeight;
                if (data.font && data.font !== 'Standard') normalized.font = data.font;
                if (data.justification && data.justification !== 'center') normalized.justification = data.justification;
                if (data.bold) normalized.bold = data.bold;
                if (data.italic) normalized.italic = data.italic;
                if (data.mirrored) normalized.mirrored = data.mirrored;
            }
            
            // Size fields - only include if they have meaningful values
            if (data.width && data.width > 0) normalized.width = data.width;
            if (data.height && data.height > 0) normalized.height = data.height;
            
            // Set option - only include if enabled
            if (data.isSet) normalized.isSet = data.isSet;
            
            // Color fields - only include if there are actual selections
            if (data.textColor) normalized.textColor = data.textColor;
            if (data.layerColors && data.layerColors.length > 0) normalized.layerColors = data.layerColors;
            
            // Attributes - only include if there are meaningful attributes
            if (data.attributes && Object.keys(data.attributes).length > 0) {
                normalized.attributes = data.attributes;
            }
            
            // Price data (if available)
            if (data.price) normalized.price = data.price;
            
            // Discount data (if available)
            if (data.discount) normalized.discount = data.discount;
            if (data.discountIndex) normalized.discountIndex = data.discountIndex;
            
            // Add size value for cart display
            if (data.unit === 'cm' && normalized.width && normalized.height) {
                normalized.size = `${normalized.width}x${normalized.height}`;
            }
            
            return normalized;
        },
        
        /**
         * Get data specifically for price calculation
         * @returns {object} Price calculation data
         */
        getPriceData() {
            const data = this.getUnifiedData();
            
            // Remove fields not needed for price calculation
            delete data.price;
            delete data.discountIndex;
            delete data.size; // PHP calculates this from width/height
            
            return data;
        },
        
        /**
         * Get data specifically for cart submission
         * @returns {object} Cart submission data
         */
        getCartData() {
            const data = this.getUnifiedData();

            const previewImage =
                app.CanvasRenderer && typeof app.CanvasRenderer.exportPreviewImage === 'function'
                    ? app.CanvasRenderer.exportPreviewImage()
                    : '';

            if (previewImage) {
                data.customProductImage = previewImage;
                data.previewImage = previewImage;
            }
            
            // Add isForText flag for PHP processing
            const builderType = app.Store.getState('builderType');
            data.isForText = ['text', 'image-text', 'flag', 'sticker-2v2-layout', 'sticker-2v3-layout'].includes(builderType);
            
            return data;
        },
        
        /**
         * Trigger a price update calculation
         * Used by variable product integration and other components
         */
        triggerPriceUpdate() {
            if (app.Utils && app.Utils.debouncedCalculatePriceAjax) {
                app.Utils.debouncedCalculatePriceAjax();
            }
        }
    };
})(window.app = window.app || {});
